import { useCallback } from "react";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { Paginated, PaginationQuery, UpdateOrderRequestBody, UpdateVisibilityRequestBody } from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import { AdminRoofCategory, UpsertAdminRoofCategoryBody } from "./types/roofCategories";

export const useRoofCategoriesApi = () => {
  const api = useApi();

  const getAdminRoofCategory = useCallback(
    (signal: AbortSignal) => {
      const url = `/admin/settings/patrol/roof_categories`;
      return useSWRMutation<AdminRoofCategory, CustomError, string, string>(url, (url, { arg }: { arg: string }) =>
        api.get<AdminRoofCategory>({ url: `${url}/${arg}`, signal }),
      );
    },
    [api],
  );

  const useAdminRoofCategories = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl("/admin/settings/patrol/roof_categories", query);
      return useSWR<Paginated<AdminRoofCategory>, CustomError, string>(url, (url) =>
        api.get<Paginated<AdminRoofCategory>>({ url, signal }),
      );
    },
    [api],
  );

  const insertAdminRoofCategory = useCallback(
    async (body: UpsertAdminRoofCategoryBody): Promise<{ id: string }> => {
      const url = `/admin/settings/patrol/roof_categories`;
      const id = await api.post<{ id: string }, UpsertAdminRoofCategoryBody>(url, body);
      mutateCache([url]);
      return id;
    },
    [api],
  );

  const updateAdminRoofCategory = useCallback(
    async (roofCategoryId: string, body: UpsertAdminRoofCategoryBody): Promise<void> => {
      const url = `/admin/settings/patrol/roof_categories/${roofCategoryId}`;
      (await api.put<UpsertAdminRoofCategoryBody>(url, body), mutateCache(["/admin/settings/patrol/roof_categories"]));
    },
    [api],
  );

  const deleteAdminRoofCategory = useCallback(
    async (roofCategoryId: string): Promise<void> => {
      const url = `/admin/settings/patrol/roof_categories/${roofCategoryId}`;
      await api.del(url);
      mutateCache([`/admin/settings/patrol/roof_categories`]);
    },
    [api],
  );

  const updateAdminRoofCategoryVisibility = useCallback(
    async (roofCategoryId: string, visibility: UpdateVisibilityRequestBody): Promise<void> => {
      const url = `/admin/settings/patrol/roof_categories/${roofCategoryId}/visibility`;
      await api.put<UpdateVisibilityRequestBody>(url, visibility);
      mutateCache(["/admin/settings/patrol/roof_categories"]);
    },
    [api],
  );

  const updateAdminRoofCategoryOrder = useCallback(
    async (roofCategoryId: string, order: UpdateOrderRequestBody): Promise<void> => {
      const url = `/admin/settings/patrol/roof_categories/${roofCategoryId}/order`;
      await api.put<UpdateOrderRequestBody>(url, order);
      mutateCache(["/admin/settings/patrol/roof_categories"]);
    },
    [api],
  );

  return {
    getAdminRoofCategory,
    useAdminRoofCategories,
    insertAdminRoofCategory,
    updateAdminRoofCategory,
    updateAdminRoofCategoryVisibility,
    updateAdminRoofCategoryOrder,
    deleteAdminRoofCategory,
  };
};
